import { onBeforeUnmount, onMounted } from 'vue';
import { gsap, prefersReducedMotion } from '@/features/hr-workspace/utils/gsapOrg';

const SCROLL_SELECTOR = '.sidebar-root__scroll';
const VAR_WIDTH = '--sidebar-scrollbar-width';
const VISIBLE_CLASS = 'sidebar-root__scroll--sb-visible';

/**
 * Show/hide sidebar scrollbar on hover via GSAP width tween (no color fade).
 *
 * @param {import('vue').Ref<HTMLElement|null>} sidebarRef
 */
export function useSidebarScrollMotion(sidebarRef) {
  let tween = null;
  let root = null;
  let scrollEl = null;

  const killTween = () => {
    if (tween) {
      tween.kill();
      tween = null;
    }
  };

  const isMotionLocked = () =>
    Boolean(root?.classList.contains('sidebar-root--motion-lock'));

  const showScrollbar = () => {
    if (!scrollEl || isMotionLocked()) return;
    killTween();
    scrollEl.classList.add(VISIBLE_CLASS);

    if (prefersReducedMotion()) {
      gsap.set(scrollEl, { [VAR_WIDTH]: '6px' });
      return;
    }

    tween = gsap.to(scrollEl, {
      [VAR_WIDTH]: '6px',
      duration: 0.28,
      ease: 'sine.out',
      overwrite: 'auto',
      onComplete: () => {
        tween = null;
      },
    });
  };

  const hideScrollbar = () => {
    if (!scrollEl) return;
    killTween();

    const finishHide = () => {
      scrollEl?.classList.remove(VISIBLE_CLASS);
      tween = null;
    };

    if (prefersReducedMotion()) {
      gsap.set(scrollEl, { [VAR_WIDTH]: '0px' });
      finishHide();
      return;
    }

    tween = gsap.to(scrollEl, {
      [VAR_WIDTH]: '0px',
      duration: 0.22,
      delay: 0.06,
      ease: 'sine.in',
      overwrite: 'auto',
      onComplete: finishHide,
    });
  };

  const onEnter = () => showScrollbar();
  const onLeave = () => hideScrollbar();

  onMounted(() => {
    root = sidebarRef.value;
    if (!root) return;

    scrollEl = root.querySelector(SCROLL_SELECTOR);
    if (!scrollEl) return;

    gsap.set(scrollEl, { [VAR_WIDTH]: '0px' });
    scrollEl.classList.remove(VISIBLE_CLASS);
    root.addEventListener('mouseenter', onEnter);
    root.addEventListener('mouseleave', onLeave);
  });

  onBeforeUnmount(() => {
    killTween();
    root?.removeEventListener('mouseenter', onEnter);
    root?.removeEventListener('mouseleave', onLeave);
    if (scrollEl) {
      scrollEl.classList.remove(VISIBLE_CLASS);
      gsap.set(scrollEl, { clearProps: VAR_WIDTH });
    }
    root = null;
    scrollEl = null;
  });
}
